<script lang="ts">
  import Layout from "../../components/layout/Layout.svelte";
  import TripSubNav from "../../components/navigation/TripSubNav.svelte";
  import { addNotification } from "../../lib/stores";
  import type { Trip } from "../../lib/types";

  let { trip, user = null, companions = [] }: {
    trip: Trip;
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
    companions?: Array<{
      id: string;
      role: string;
      joined_at: string;
      user: { id: string; name: string; nickname: string | null; avatar_url: string | null };
    }>;
  } = $props();

  const isOwner = $derived(trip.userId === user?.id);

  type CompanionItem = {
    id: string;
    role: string;
    joined_at: string;
    user: { id: string; name: string; nickname: string | null; avatar_url: string | null };
  };

  function normalizeCompanion(companion: any): CompanionItem {
    return {
      id: companion.id,
      role: companion.role,
      joined_at: companion.joined_at || companion.joinedAt,
      user: {
        id: companion.user.id,
        name: companion.user.name,
        nickname: companion.user.nickname ?? null,
        avatar_url: companion.user.avatar_url || companion.user.avatarUrl || null,
      },
    };
  }

  let localCompanions = $state<CompanionItem[]>([]);
  let showAddModal = $state(false);
  let searchQuery = $state("");
  let searchResults: Array<{ id: string; name: string; nickname: string | null; avatar_url: string | null }> = $state([]);
  let searching = $state(false);
  let addingUserId: string | null = $state(null);
  let selectedRole = $state("member");
  let searchTimeout: ReturnType<typeof setTimeout>;
  let removingId: string | null = $state(null);
  let companionsHydrated = false;

  $effect(() => {
    if (companionsHydrated) return;
    localCompanions = companions.map(normalizeCompanion);
    companionsHydrated = true;
  });

  const ROLE_LABELS: Record<string, string> = {
    owner: "소유자",
    editor: "편집자",
    member: "멤버",
    viewer: "뷰어",
  };

  const ROLE_COLORS: Record<string, { bg: string; color: string }> = {
    owner: { bg: "rgba(32,178,170,0.15)", color: "#20b2aa" },
    editor: { bg: "rgba(96,165,250,0.15)", color: "#60a5fa" },
    member: { bg: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" },
    viewer: { bg: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.35)" },
  };

  function handleSearchInput() {
    clearTimeout(searchTimeout);
    if (searchQuery.trim().length < 2) {
      searchResults = [];
      return;
    }
    searchTimeout = setTimeout(doSearch, 300);
  }

  async function doSearch() {
    searching = true;
    try {
      const res = await fetch(`/api/v1/users?search=${encodeURIComponent(searchQuery)}`);
      if (res.ok) {
        const data = await res.json();
        const existingIds = new Set(localCompanions.map(c => c.user.id));
        searchResults = (data.data || []).filter((u: { id: string }) => !existingIds.has(u.id));
      }
    } catch {
      addNotification("error", "검색에 실패했습니다.");
    } finally {
      searching = false;
    }
  }

  async function addCompanion(targetUser: { id: string; name: string; nickname: string | null; avatar_url: string | null }) {
    addingUserId = targetUser.id;
    try {
      const res = await fetch(`/api/v1/trips/${trip.id}/companions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: targetUser.id, role: selectedRole }),
      });
      if (res.ok) {
        const data = await res.json();
        localCompanions = [...localCompanions, normalizeCompanion(data.data)];
        searchResults = searchResults.filter(u => u.id !== targetUser.id);
        addNotification("success", `${targetUser.name}님을 추가했습니다.`);
      } else {
        const err = await res.json();
        addNotification("error", err.error?.errors?.[0]?.message || "추가에 실패했습니다.");
      }
    } catch {
      addNotification("error", "오류가 발생했습니다.");
    } finally {
      addingUserId = null;
    }
  }

  async function removeCompanion(companion: CompanionItem) {
    if (!confirm(`${companion.user.name}님을 제거하시겠습니까?`)) return;
    removingId = companion.id;
    try {
      const res = await fetch(`/api/v1/trips/${trip.id}/companions/${companion.id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        localCompanions = localCompanions.filter(c => c.id !== companion.id);
        addNotification("success", `${companion.user.name}님을 제거했습니다.`);
      } else {
        const err = await res.json();
        addNotification("error", err.error?.errors?.[0]?.message || "제거에 실패했습니다.");
      }
    } catch {
      addNotification("error", "오류가 발생했습니다.");
    } finally {
      removingId = null;
    }
  }

  function closeModal() {
    showAddModal = false;
    searchQuery = "";
    searchResults = [];
    selectedRole = "member";
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString("ko-KR", { year: "numeric", month: "short", day: "numeric" });
  }
</script>

<Layout {user}>
  <TripSubNav {trip} currentPage="companions" />

  <div class="max-w-2xl mx-auto px-4 py-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-xl font-bold" style="color: var(--text-primary);">동행자 관리</h2>
        <p class="text-sm mt-0.5" style="color: var(--text-muted);">총 {localCompanions.length}명</p>
      </div>
      {#if isOwner}
        <button
          onclick={() => (showAddModal = true)}
          class="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
          style="background: var(--accent); color: #000;"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
          동행자 추가
        </button>
      {/if}
    </div>

    <!-- Companion List -->
    {#if localCompanions.length === 0}
      <div class="text-center py-24">
        <div class="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center" style="background: var(--surface);">
          <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        </div>
        <h3 class="font-semibold mb-2" style="color: var(--text-primary);">동행자가 없습니다</h3>
        <p class="text-sm" style="color: var(--text-muted);">친구를 초대해 함께 여행을 계획해보세요</p>
      </div>
    {:else}
      <div class="space-y-3">
        {#each localCompanions as companion}
          <div
            class="flex items-center gap-4 p-4 rounded-2xl transition-all"
            style="background: var(--surface); border: 1px solid var(--border);"
          >
            <!-- Avatar -->
            {#if companion.user.avatar_url}
              <img src={companion.user.avatar_url} alt={companion.user.name} class="w-12 h-12 rounded-full object-cover flex-shrink-0" />
            {:else}
              <div
                class="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold flex-shrink-0"
                style="background: var(--accent-dim); color: var(--accent);"
              >
                {companion.user.name.charAt(0)}
              </div>
            {/if}

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <a href={`/users/${companion.user.id}`} class="font-semibold hover:underline truncate" style="color: var(--text-primary);">
                  {companion.user.name}
                </a>
                <span
                  class="flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium"
                  style="background: {(ROLE_COLORS[companion.role] ?? ROLE_COLORS.member).bg}; color: {(ROLE_COLORS[companion.role] ?? ROLE_COLORS.member).color};"
                >
                  {ROLE_LABELS[companion.role] || companion.role}
                </span>
              </div>
              {#if companion.user.nickname}
                <p class="text-xs mt-0.5 truncate" style="color: var(--text-muted);">@{companion.user.nickname}</p>
              {/if}
              <p class="text-xs mt-0.5" style="color: var(--text-muted);">{formatDate(companion.joined_at)} 참여</p>
            </div>

            <!-- Remove button (owner only, can't remove owner) -->
            {#if isOwner && companion.role !== "owner" && companion.user.id !== user?.id}
              <button
                onclick={() => removeCompanion(companion)}
                disabled={removingId === companion.id}
                class="flex-shrink-0 p-2 rounded-xl transition-colors disabled:opacity-50"
                style="color: #f87171;"
                title="제거"
                onmouseenter={(e) => (e.currentTarget as HTMLElement).style.background = 'rgba(248,113,113,0.1)'}
                onmouseleave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}
              >
                {#if removingId === companion.id}
                  <svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                {:else}
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                  </svg>
                {/if}
              </button>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
  </div>
</Layout>

<!-- Add Companion Modal -->
{#if showAddModal}
  <div
    class="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
    style="background: rgba(0,0,0,0.7); backdrop-filter: blur(4px);"
    onclick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
    onkeydown={(e) => { if (e.key === "Escape") closeModal(); }}
    role="dialog"
    aria-modal="true"
    tabindex="-1"
  >
    <div
      class="w-full sm:max-w-md rounded-3xl p-6 space-y-5"
      style="background: #1c1c1e; border: 1px solid rgba(255,255,255,0.1); max-height: 85vh; overflow-y: auto;"
    >
      <!-- Modal Header -->
      <div class="flex items-center justify-between">
        <h3 class="text-lg font-bold" style="color: var(--text-primary);">동행자 추가</h3>
        <button
          aria-label="모달 닫기"
          onclick={closeModal}
          class="p-2 rounded-xl transition-colors"
          style="color: var(--text-muted);"
          onmouseenter={(e) => (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'}
          onmouseleave={(e) => (e.currentTarget as HTMLElement).style.background = 'transparent'}
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Search Input -->
      <div class="relative">
        <div class="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          {#if searching}
            <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24" style="color: var(--accent);">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          {:else}
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--text-muted);">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          {/if}
        </div>
        <input
          type="text"
          bind:value={searchQuery}
          oninput={handleSearchInput}
          placeholder="이름 또는 닉네임으로 검색"
          class="w-full pl-10 pr-4 py-3 rounded-xl text-sm outline-none"
          style="background: rgba(255,255,255,0.06); color: var(--text-primary); border: 1px solid var(--border);"
        />
      </div>

      <!-- Role Selector -->
      <div>
        <span class="block text-xs font-medium mb-2" style="color: var(--text-muted);">권한</span>
        <div class="grid grid-cols-3 gap-2" role="group" aria-label="동행자 권한 선택">
          {#each [
            { value: "member", label: "멤버" },
            { value: "editor", label: "편집자" },
            { value: "viewer", label: "뷰어" },
          ] as role}
            <button
              type="button"
              onclick={() => (selectedRole = role.value)}
              class="py-2 rounded-xl text-sm font-medium transition-all"
              style="background: {selectedRole === role.value ? 'var(--accent-dim)' : 'rgba(255,255,255,0.04)'}; color: {selectedRole === role.value ? 'var(--accent)' : 'var(--text-secondary)'}; border: 1px solid {selectedRole === role.value ? 'rgba(32,178,170,0.3)' : 'transparent'};"
            >
              {role.label}
            </button>
          {/each}
        </div>
      </div>

      <!-- Search Results -->
      {#if searchResults.length > 0}
        <div class="space-y-2 max-h-60 overflow-y-auto">
          {#each searchResults as result}
            <div class="flex items-center gap-3 p-3 rounded-xl" style="background: rgba(255,255,255,0.04);">
              {#if result.avatar_url}
                <img src={result.avatar_url} alt={result.name} class="w-10 h-10 rounded-full object-cover flex-shrink-0" />
              {:else}
                <div class="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0"
                  style="background: var(--accent-dim); color: var(--accent);">
                  {result.name.charAt(0)}
                </div>
              {/if}
              <div class="flex-1 min-w-0">
                <p class="font-medium text-sm truncate" style="color: var(--text-primary);">{result.name}</p>
                {#if result.nickname}
                  <p class="text-xs truncate" style="color: var(--text-muted);">@{result.nickname}</p>
                {/if}
              </div>
              <button
                onclick={() => addCompanion(result)}
                disabled={addingUserId === result.id}
                class="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
                style="background: var(--accent); color: #000;"
              >
                {addingUserId === result.id ? "추가 중..." : "추가"}
              </button>
            </div>
          {/each}
        </div>
      {:else if searchQuery.length >= 2 && !searching}
        <div class="text-center py-6" style="color: var(--text-muted);">
          <p class="text-sm">검색 결과가 없습니다</p>
        </div>
      {:else if searchQuery.length < 2}
        <div class="text-center py-6" style="color: var(--text-muted);">
          <p class="text-sm">2자 이상 입력하세요</p>
        </div>
      {/if}
    </div>
  </div>
{/if}
